import { useState, useEffect, useCallback } from "react";
import HelpButton from "../components/HelpButton";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { Link, ArrowsClockwise, Prohibit, ClipboardText, ArrowSquareOut, MagnifyingGlass } from "@phosphor-icons/react";

const DOC_TYPE_LABELS = {
  "quotation": "Quotation", "invoice": "Invoice", "receipt": "Receipt",
  "purchase-order": "Purchase Order", "delivery-challan": "Delivery Challan",
  "installation-completion": "Installation Completion", "customer-statement": "Customer Statement",
};

const PublicLinks = ({ user, logout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.set("doc_type", filterType);
      if (filterStatus) params.set("status", filterStatus);
      const r = await api.get(`/public-tokens?${params.toString()}`);
      setItems(r.data || []);
    } catch (e) { toast.error("Failed to load tokens"); }
    finally { setLoading(false); }
  }, [filterType, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const copyLink = async (token, doc_type) => {
    const url = `${window.location.origin}/public/${doc_type}/${token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Public link copied"); }
    catch { toast.error("Couldn't copy"); }
  };

  const revoke = async (t) => {
    if (!window.confirm(`Revoke link for ${t.doc_type} ${t.doc_number || ""}? Recipients will lose access.`)) return;
    try {
      await api.put(`/public-tokens/${t.token}/revoke`);
      toast.success("Revoked");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const regen = async (t) => {
    if (!window.confirm(`Regenerate link for ${t.doc_type} ${t.doc_number || ""}? Old link stops working immediately.`)) return;
    try {
      const r = await api.put(`/public-tokens/${t.token}/regenerate`);
      toast.success("New link generated — copying to clipboard");
      const newToken = r.data?.token_doc?.token;
      if (newToken) await copyLink(newToken, t.doc_type);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const visible = items.filter(t => {
    if (search) {
      const s = search.toLowerCase();
      return (t.doc_number || "").toLowerCase().includes(s) || (t.customer_name || "").toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="p-8">
          <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">Document Sharing</div>
              <div className="flex items-center"><h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                Public Links
              </h1><HelpButton moduleKey="admin_settings" /></div>
              <p className="text-base leading-relaxed text-slate-700">{items.length} tokenised links · view, copy, revoke or regenerate</p>
            </div>
            <button data-testid="links-refresh-btn" onClick={load} className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium">Refresh</button>
          </div>

          <div data-testid="links-filters" className="bg-white border border-slate-200 p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2 relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="links-search" type="text" placeholder="Search doc # or customer…" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none border border-slate-300 focus:border-[#002FA7] p-2 pl-9 w-full outline-none text-sm" />
            </div>
            <select data-testid="links-filter-type" value={filterType} onChange={(e) => setFilterType(e.target.value)} className="rounded-none border border-slate-300 p-2 outline-none text-sm">
              <option value="">All doc types</option>
              {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select data-testid="links-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-none border border-slate-300 p-2 outline-none text-sm">
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="loading-spinner"></div></div>
          ) : (
            <div data-testid="links-table" className="bg-white border border-slate-200 shadow-sm overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Type</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Doc #</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Customer / Vendor</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Opens</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Last Access</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Status</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 ? (
                    <tr><td colSpan={7} className="p-12 text-center text-slate-400 text-sm">No public links yet. Generate one from any quotation, invoice, PO etc.</td></tr>
                  ) : visible.map((t, i) => (
                    <tr key={t.token} data-testid={`link-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="p-3"><span className="text-[10px] uppercase tracking-wider font-bold bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5">{DOC_TYPE_LABELS[t.doc_type] || t.doc_type}</span></td>
                      <td className="p-3 font-mono text-xs">{t.doc_number || "—"}</td>
                      <td className="p-3 text-sm">{t.customer_name || "—"}</td>
                      <td className="p-3 text-right tabular-nums">{t.access_count || 0}</td>
                      <td className="p-3 text-xs text-slate-500">{(t.last_accessed_at || "").slice(0, 16).replace("T", " ") || <span className="text-slate-400">Never</span>}</td>
                      <td className="p-3"><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${t.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>{t.status}</span></td>
                      <td className="p-3 text-right">
                        <a data-testid={`link-open-${i}`} href={`/public/${t.doc_type}/${t.token}`} target="_blank" rel="noreferrer" className="p-1.5 text-[#002FA7] hover:bg-blue-50 inline-block" title="Open"><ArrowSquareOut size={14} weight="bold" /></a>
                        <button data-testid={`link-copy-${i}`} onClick={() => copyLink(t.token, t.doc_type)} className="p-1.5 text-slate-700 hover:bg-slate-100 ml-1" title="Copy link"><ClipboardText size={14} weight="bold" /></button>
                        {t.status === "active" && (
                          <>
                            <button data-testid={`link-regen-${i}`} onClick={() => regen(t)} className="p-1.5 text-amber-700 hover:bg-amber-50 ml-1" title="Regenerate"><ArrowsClockwise size={14} weight="bold" /></button>
                            <button data-testid={`link-revoke-${i}`} onClick={() => revoke(t)} className="p-1.5 text-rose-700 hover:bg-rose-50 ml-1" title="Revoke"><Prohibit size={14} weight="bold" /></button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicLinks;
